import { Directive, HostBinding, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';

import { A11yRole } from '../interfaces/a11y.interfaces';
import { A11yKeyboardService } from '../services/a11y-keyboard.service';
import { createA11yId, normalizeAriaValue, toAriaBoolean } from '../utils/a11y.util';

import { A11yTablistDirective } from './a11y-tabs.directive';

@Directive({
  selector: '[khA11yTab]'
})
export class A11yTabDirective implements OnInit, OnDestroy, A11yRole {
  private readonly tablist = inject(A11yTablistDirective, { optional: true });
  private readonly keyboardService = inject(A11yKeyboardService);
  private readonly generatedId = createA11yId('kh-a11y-tab');

  @Input('khA11yTab') public selected: boolean | string | null = null;
  @Input() public khA11yTabId: string | null = null;
  @Input() public khA11yTabControls: string | null = null;

  @HostBinding('attr.role') public readonly role = 'tab';

  @HostBinding('attr.id')
  public get id(): string {
    return normalizeAriaValue(this.khA11yTabId) ?? this.generatedId;
  }

  @HostBinding('attr.aria-selected')
  public get ariaSelected(): 'true' | 'false' | null {
    return toAriaBoolean(this.selected);
  }

  @HostBinding('attr.aria-controls')
  public get ariaControls(): string | null {
    return normalizeAriaValue(this.khA11yTabControls) ?? this.tablist?.getPanelIdForTab(this.id) ?? null;
  }

  @HostBinding('attr.tabindex')
  public get tabindex(): string {
    return this.selected === true || this.selected === 'true' ? '0' : '-1';
  }

  public ngOnInit(): void {
    this.tablist?.registerTab(this.id);
  }

  @HostListener('keydown', ['$event'])
  public onA11yKeydown(event: KeyboardEvent): void {
    this.keyboardService.activateOnEnterOrSpace(event, () => {
      (event.currentTarget as HTMLElement).click();
    });
  }

  public ngOnDestroy(): void {
    this.tablist?.unregisterTab(this.id);
  }
}
